import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { Icon } from "@circle-react-shared/Icon";
import type { Space } from "@circle-react/types/Space";
import { groupBy, uniq } from "lodash";
import { useMemo } from "react";
import type { ReactNode } from "react";

import { t } from "@/i18n-js/instance";
import { useThemeContext } from "@/react/providers/Theme";

const HOMEPAGE_SPACE_VALUE = 0;
const SPACE_ICON_CLASSNAME = "!h-5 !w-5 shrink-0";
const getUngroupedLabel = () => t("space_select_compass.ungrouped_label");

interface SpaceOption {
  label: string;
  prefix?: ReactNode;
  value: number;
}

interface SpaceOptionGroup {
  items: SpaceOption[];
  label: string;
}

interface UseSpaceSelectOptionsParams {
  shouldDisplayHomepage?: boolean;
  shouldShowEmoji?: boolean;
  spaces: Space[];
  value?: number | null;
}

export const useSpaceSelectOptions = ({
  spaces,
  value,
  shouldDisplayHomepage,
  shouldShowEmoji = true,
}: UseSpaceSelectOptionsParams) => {
  const { currentAppearance } = useThemeContext();

  const options = useMemo(() => {
    const safeSpaces = spaces ?? [];
    const spaceToOption = (space: Space): SpaceOption => {
      const prefix = shouldShowEmoji ? (
        <EmojiRenderer
          className={SPACE_ICON_CLASSNAME}
          emoji={space.emoji}
          appearance={currentAppearance}
          customEmojiUrl={space.custom_emoji_url}
          customEmojiDarkUrl={space.custom_emoji_dark_url}
        />
      ) : undefined;

      return {
        value: space.id,
        label: space.name ?? "",
        ...(prefix && { prefix }),
      };
    };

    const getGroupName = (space: Space) =>
      space.space_group_name ?? getUngroupedLabel();

    const grouped = groupBy(safeSpaces, getGroupName);
    const groupNames = uniq(safeSpaces.map(getGroupName));

    const groups: SpaceOptionGroup[] = groupNames.map(groupName => ({
      label: groupName,
      items: grouped[groupName].map(spaceToOption),
    }));

    if (shouldDisplayHomepage) {
      const feedLabel = t("feed");
      return [
        {
          label: feedLabel,
          items: [
            {
              value: HOMEPAGE_SPACE_VALUE,
              label: feedLabel,
              prefix: (
                <Icon
                  type="20-feed-v3"
                  className={SPACE_ICON_CLASSNAME}
                  size={20}
                />
              ),
            },
          ],
        },
        ...groups,
      ];
    }

    return groups;
  }, [spaces, shouldDisplayHomepage, shouldShowEmoji, currentAppearance]);

  const selectedOption = useMemo(
    (): SpaceOption | null =>
      options
        .flatMap(group => group.items)
        .find(option => option.value === value) ?? null,
    [options, value],
  );

  return { options, selectedOption };
};
